import React, { useState } from 'react'
import { CalendarClock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { localToday } from '@/services/api/integrations/useXeroMapping'
import {
  type InvoiceNeedingDate,
  useScheduleInvoiceBalance,
} from '@/services/api/integrations/useBankFeed'
import { formatDate, money } from './BankFeedPanel'

/**
 * Overdue invoices that were part-paid: the rest is not forecast until the
 * user says when (and how much) they expect it.
 */
export default function NeedsInputList({
  invoices,
  canEdit,
}: {
  invoices: InvoiceNeedingDate[]
  canEdit: boolean
}) {
  if (invoices.length === 0) return null

  return (
    <Card className="space-y-3 border-amber-200 p-4">
      <div className="flex items-center gap-2">
        <CalendarClock className="size-4 text-amber-600" />
        <h4 className="text-sm font-semibold">Needs your input</h4>
      </div>
      <p className="text-sm text-muted-foreground">
        These overdue invoices were part-paid. The rest isn't in your forecast
        until you say when you expect it.
      </p>
      <ul className="divide-y">
        {invoices.map((invoice) => (
          <NeedsInputRow key={invoice.id} invoice={invoice} canEdit={canEdit} />
        ))}
      </ul>
    </Card>
  )
}

function NeedsInputRow({
  invoice,
  canEdit,
}: {
  invoice: InvoiceNeedingDate
  canEdit: boolean
}) {
  const schedule = useScheduleInvoiceBalance()
  const [date, setDate] = useState(localToday())
  const [amount, setAmount] = useState(String(invoice.balance))
  const valid = !!date && date >= localToday() && Number(amount) > 0

  return (
    <li className="flex flex-col gap-2 py-3 md:flex-row md:items-end md:justify-between">
      <div className="min-w-0 text-sm">
        <p className="font-medium">{invoice.description || 'Invoice'}</p>
        <p className="text-muted-foreground">
          {money(invoice.balance)} of {money(invoice.original_amount)} still{' '}
          {invoice.direction === 'in' ? 'to receive' : 'to pay'}
          {invoice.due_date && ` · was due ${formatDate(invoice.due_date)}`}
          {invoice.category_name && ` · ${invoice.category_name}`}
        </p>
      </div>
      {canEdit && (
        <div className="flex flex-wrap items-end gap-2">
          <label className="block">
            <span className="mb-1 block text-xs text-muted-foreground">
              Expected on
            </span>
            <Input
              type="date"
              min={localToday()}
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </label>
          <label className="block w-32">
            <span className="mb-1 block text-xs text-muted-foreground">
              Amount
            </span>
            <Input
              type="number"
              min="0.01"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </label>
          <Button
            size="md"
            disabled={!valid || schedule.isPending}
            onClick={() =>
              schedule.mutate({
                estimateId: invoice.id,
                date,
                amount: Number(amount),
              })
            }
          >
            Forecast
          </Button>
        </div>
      )}
    </li>
  )
}
